<template>
  <div>
    <h1>Detalle del Contrato</h1>
    <div v-if="loading">Cargando...</div>
    <div v-else-if="error">Error: {{ error }}</div>
    <div v-else>
      <!-- Table A: ar* fields -->
      <h2>Arrendatario</h2>
      <table class="detail-table">
        <tbody>
          <tr v-for="(value, key) in arFields" :key="key">
            <td class="item">{{ arHeaderLabel(key) }}</td>
            <td class="value">{{ formatValue(key, value) }}</td>
          </tr>
        </tbody>
      </table>

      <!-- Tables B and C: pr* and co* fields side by side -->
      <div class="side-by-side">
        <div>
          <h2>Propiedad</h2>
          <table class="detail-table">
            <tbody>
              <tr v-for="(value, key, idx) in prFields" :key="key" :class="{ 'odd-row': idx % 2 === 0, 'even-row': idx % 2 !== 0 }">
                <td class="item">{{ prHeaderLabel(key) }}</td>
                <td class="value">{{ formatValue(key, value) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div>
          <div class="co-header">
            <h2>Contrato</h2>
            <button v-if="!showEditContrato && puedeEditarContrato" class="button-71 small-btn" @click="openEditContrato">Editar</button>
          </div>
          <table class="detail-table" v-if="!showEditContrato">
            <tbody>
              <tr v-for="(value, key, idx) in coFields" :key="key" :class="{ 'odd-row': idx % 2 === 0, 'even-row': idx % 2 !== 0 }">
                <td class="item">{{ coHeaderLabel(key) }}</td>
                <td class="value" :class="{ 'monto-destacado': key === 'co_monto_clp' || key === 'co_monto_uf' }">{{ formatCoValue(key, value) }}</td>
              </tr>
            </tbody>
          </table>

          <form v-else @submit.prevent="saveEditContrato" class="edit-contrato-form">
            <div>
              <label>Fecha de inicio</label>
              <input type="date" v-model="editForm.co_fecha_inicio" required />
            </div>
            <div>
              <label>Fecha de término</label>
              <input type="date" v-model="editForm.co_fecha_termino" required />
            </div>
            <div>
              <label>Tipo de contrato</label>
              <select v-model.number="editForm.co_tipo" required>
                <option :value="0">UF</option>
                <option :value="1">EE</option>
                <option :value="2">CLP</option>
              </select>
            </div>
            <div>
              <label>Monto (CLP)</label>
              <input type="number" step="0.01" min="0" v-model="editForm.co_monto_clp" required />
            </div>
            <div>
              <label>Monto (UF)</label>
              <input type="number" step="0.01" min="0" v-model="editForm.co_monto_uf" required />
            </div>
            <div>
              <label>Día de pago</label>
              <input type="number" step="1" min="1" max="31" v-model="editForm.co_dia_de_pago" required />
            </div>
            <div>
              <label>Días de aviso de término</label>
              <input type="number" step="1" min="0" v-model="editForm.co_dias_aviso_termino" required />
            </div>
            <div>
              <label>Fecha de aviso</label>
              <input type="date" v-model="editForm.co_fecha_aviso" />
            </div>
            <div>
              <label>Garantía</label>
              <input type="number" step="0.01" min="0" v-model="editForm.co_garantia" required />
            </div>
            <div>
              <label>Fecha de garantía</label>
              <input type="date" v-model="editForm.co_fecha_garantia" />
            </div>
            <div>
              <label>Cuenta de depósito</label>
              <input type="text" v-model="editForm.co_cuenta_deposito" required />
            </div>
            <div>
              <label>Inventario</label>
              <input type="text" v-model="editForm.co_inventario" />
            </div>
            <div>
              <label>Contrato vigente</label>
              <select v-model.number="editForm.co_vigente">
                <option :value="1">Sí</option>
                <option :value="0">No</option>
              </select>
            </div>
            <div>
              <label>Notas</label>
              <textarea v-model="editForm.co_notas" rows="3"></textarea>
            </div>
            <p v-if="editError" class="error-msg">{{ editError }}</p>
            <div style="display: flex; gap: 1em; margin-top: 1em;">
              <button class="button-71 grey-btn" type="button" @click="showEditContrato = false" :disabled="savingEdit">Cancelar</button>
              <button class="button-71 green-btn" type="submit" :disabled="savingEdit">{{ savingEdit ? 'Guardando...' : 'Guardar cambios' }}</button>
            </div>
          </form>
        </div>
      </div>

      <!-- CobroPago table (horizontal, dynamic headers) above Pagos -->
      <div>
        <div class="co-header">
          <h2>Cobro/Pago</h2>
          <button v-if="!showCargarCobros && puedeEditarCobros" class="button-71 small-btn" @click="showCargarCobros = true">Cargar Cobros desde Fecha</button>
        </div>

        <div v-if="showCargarCobros" class="cargar-cobros-form">
          <label>Fecha de inicio del nuevo período:</label>
          <input type="date" v-model="fechaCargarCobros" />
          <div class="cargar-cobros-actions">
            <button class="button-71 grey-btn" type="button" @click="showCargarCobros = false" :disabled="cargandoCobros">Cancelar</button>
            <button class="button-71 green-btn" type="button" @click="cargarCobros(false)" :disabled="cargandoCobros || !fechaCargarCobros">
              {{ cargandoCobros ? 'Cargando...' : 'Cargar Cobros' }}
            </button>
          </div>
          <p v-if="cargarCobrosError" class="error-msg">{{ cargarCobrosError }}</p>
        </div>

        <table class="data-table horizontal-table" v-if="cobroPagoArray.length">
          <thead>
            <tr>
              <th v-for="col in cobroPagoVisibleColumns" :key="col">{{ cobroPagoHeaderLabel(col) }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, idx) in cobroPagoArray" :key="row.cb_id || idx"
                :class="[
                  row.cb_pagado === 0 && isPastDue(row.cb_fecha_vencimiento) ? 'row-red' : '',
                  row.cb_pagado === 0 && isFutureDue(row.cb_fecha_vencimiento) ? 'row-green' : '',
                  idx % 2 === 0 ? 'odd-row' : 'even-row'
                ]">
              <td v-for="col in cobroPagoVisibleColumns" :key="col" :style="isNumberColumn(col) ? 'text-align: right;' : ''">
                {{ formatCobroPagoValue(col, row[col]) }}
                <template v-if="col === 'cb_pagado' && row.cb_pagado === 0">
                  <button v-if="puedeEditarCobros" class="button-71 small-btn" @click="goToPago(row)">Registrar Pago</button>
                </template>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-else>No hay datos de cobro/pago.</div>
      </div>

      <div class="side-by-side-tables">
        <div class="pagos-table-container">
          <h2>Pagos</h2>
          <table class="data-table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Monto</th>
                <th>Monto UF</th>
                <th>Periodo</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(pago, idx) in pagosSorted" :key="pago.pa_id" :class="{ 'odd-row': idx % 2 === 0, 'even-row': idx % 2 !== 0 }">
                <td>{{ pago.pa_fecha }}</td>
                <td style="text-align: right;">{{ formatNumber(pago.pa_monto) }}</td>
                <td style="text-align: right;">{{ formatNumber(pago.pa_monto_uf) }}</td>
                <td>{{ pago.pa_periodo }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <PagoForm
        v-if="showPagoForm"
        :coId="contrato.co_id"
        @submit="handlePagoSubmit"
        @close="showPagoForm = false"
      />
    </div>
  </div>
</template>

<script>
import apiClient from '../../api/index';
import PagoForm from '@/components/PagoForm.vue';

export default {
  name: 'ContratoDetalle',
  components: {
    PagoForm,
  },
  data() {
    return {
      contrato: {},
      pagos: [],
      cobros: [],
      cobroPago: [],
      loading: true,
      error: null,
      showPagoForm: false,
      showEditContrato: false,
      editForm: {},
      savingEdit: false,
      editError: '',
      showCargarCobros: false,
      fechaCargarCobros: '',
      cargandoCobros: false,
      cargarCobrosError: '',
    };
  },
  computed: {
    puedeEditarContrato() {
      return this.$store.getters['auth/puede']('arriendos.contratos', 'escribir');
    },
    puedeEditarCobros() {
      return this.$store.getters['auth/puede']('arriendos.cobros_pagos', 'escribir');
    },
    arFields() {
      if (!this.contrato) return {};
      const ar = {};
      for (const key in this.contrato) {
        if (key.startsWith('ar_') && key !== 'ar_id') ar[key] = this.contrato[key];
      }
      return ar;
    },
    prFields() {
      if (!this.contrato) return {};
      const pr = {};
      for (const key in this.contrato) {
        if (key.startsWith('pr_') && key !== 'pr_id') pr[key] = this.contrato[key];
      }
      return pr;
    },
    coFields() {
      if (!this.contrato) return {};
      const co = {};
      for (const key in this.contrato) {
        if (key.startsWith('co_') && key !== 'co_id') co[key] = this.contrato[key];
      }
      return co;
    },
    cobroPagoArray() {
      // Show periods with cb_fecha_vencimiento before 45 days in the future
      const today = new Date();
      const futureLimit = new Date(today);
      futureLimit.setDate(today.getDate() + 45);
      return (Array.isArray(this.cobroPago) ? this.cobroPago : []).filter(row => {
        if (!row.cb_fecha_vencimiento) return false;
        const venc = new Date(row.cb_fecha_vencimiento);
        return venc < futureLimit;
      });
    },
    cobroPagoVisibleColumns() {
      if (!this.cobroPagoArray.length) return [];
      // Collect all unique keys from all objects
      const keySet = new Set();
      this.cobroPagoArray.forEach(row => {
        Object.keys(row).forEach(key => keySet.add(key));
      });
      let allKeys = Array.from(keySet);
      // Remove unwanted columns
      const exclude = ['co_id', 'pa_id', 'pa_periodo'];
      allKeys = allKeys.filter(key => !exclude.includes(key));
      // Only show columns with at least one non-null/non-empty value
      return allKeys.filter(key =>
        this.cobroPagoArray.some(row => row[key] !== null && row[key] !== undefined && row[key] !== '')
      );
    },
    visibleCobroPagoKeys() {
      // Only show columns that are not all null/empty and are relevant
      if (!this.cobroPagoArray.length) return [];
      const allKeys = Object.keys(this.cobroPagoArray[0]);
      // Exclude internal or not relevant fields
      const exclude = ['co_id'];
      // Only include keys that have at least one non-null/non-empty value
      return allKeys.filter(key => {
        if (exclude.includes(key)) return false;
        return this.cobroPagoArray.some(row => row[key] !== null && row[key] !== undefined && row[key] !== '');
      });
    },
    pagosSorted() {
      // Return pagos sorted by pa_fecha descending
      if (!Array.isArray(this.pagos)) return [];
      return [...this.pagos].sort((a, b) => {
        // Compare as dates
        return new Date(b.pa_fecha) - new Date(a.pa_fecha);
      });
    },
  },
  methods: {
    formatNumber(val) {
      if (val === null || val === undefined || isNaN(val)) return val;
      return Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatCoValue(key, value) {
      if (value === null || value === undefined || value === '') return '-';
      const fechaFields = ['co_fecha_inicio', 'co_fecha_termino', 'co_fecha_garantia', 'co_fecha_aviso'];
      if (fechaFields.includes(key)) {
        return this.formatDateShort(value);
      }
      if (key === 'co_tipo') {
        const map = { 0: 'UF', 1: 'EE', 2: 'CLP' };
        return map[value] !== undefined ? map[value] : value;
      }
      if (key === 'co_monto_clp' || key === 'co_garantia') {
        return this.formatCurrency(value);
      }
      if (key === 'co_monto_uf') {
        return Number(value).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
      if (key === 'co_dia_de_pago' || key === 'co_dias_aviso_termino') {
        return parseInt(value, 10);
      }
      if (key === 'co_vigente') {
        return (value === 1 || value === true) ? 'TRUE' : 'FALSE';
      }
      return value;
    },
    formatDateShort(value) {
      const meses = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
      const d = new Date(value);
      if (isNaN(d.getTime())) return value;
      const dd = String(d.getUTCDate()).padStart(2, '0');
      const mmm = meses[d.getUTCMonth()];
      const yy = String(d.getUTCFullYear()).slice(-2);
      return `${dd}-${mmm}-${yy}`;
    },
    formatCurrency(value) {
      const n = Number(value);
      if (isNaN(n)) return value;
      return '$' + n.toLocaleString('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    },
    formatValue(key, value) {
      // Format only numbers that are not dates
      if (typeof value === 'number' && !key.includes('fecha')) {
        return this.formatNumber(value);
      }
      return value;
    },
    isNumber(val) {
      return val !== null && val !== undefined && !isNaN(val) && val !== '' && typeof val !== 'boolean';
    },
    formatCobroPagoValue(key, value) {
      // Format numbers, except for IDs
      if (['cb_monto', 'cb_monto_uf', 'pa_monto', 'pa_monto_uf'].includes(key)) {
        return this.formatNumber(value);
      }
      if (key === 'cb_pagado' || key === 'pa_complete_period' || key === 'pa_skip_period') {
        if (value === 1) return 'Sí';
        if (value === 0) return 'No';
      }
      return value;
    },
    cobroPagoHeaderLabel(key) {
      // Custom header labels for clarity
      const map = {
        cb_id: 'Cobro ID',
        co_id: 'Contrato',
        cb_periodo: 'Periodo',
        cb_fecha_vencimiento: 'Vencimiento',
        cb_tipo: 'Tipo Cobro',
        cb_monto: 'Monto',
        cb_monto_uf: 'Monto UF',
        cb_descripcion: 'Descripción',
        cb_pagado: 'Pagado',
        pa_id: 'Pago ID',
        pa_fecha: 'Fecha Pago',
        pa_monto: 'Monto Pago',
        pa_monto_uf: 'Monto UF Pago',
        pa_tipo: 'Tipo Pago',
        pa_complete_period: 'Completo',
        pa_skip_period: 'Saltado',
        pa_periodo: 'Periodo Pago',
        pa_notas: 'Notas',
      };
      return map[key] || key;
    },
    isNumberColumn(key) {
      // Heuristic: treat as number if key includes 'monto', 'uf', or is cb_id/pa_id
      return (
        key.includes('monto') ||
        key.includes('uf') ||
        key.endsWith('_id')
      );
    },
    arHeaderLabel(key) {
      const map = {
        ar_nombre: 'Nombre',
        ar_rut: 'RUT',
      };
      return map[key] || key;
    },
    prHeaderLabel(key) {
      const map = {
        pr_codigo: 'Código',
        pr_direccion: 'Dirección',
        pr_comuna: 'Comuna',
        pr_ciudad: 'Ciudad',
        pr_pais: 'País',
        pr_propietario: 'Propietario',
        pr_notas: 'Notas',
        pr_rol: 'Rol',
        pr_cbrs: 'CBRS',
        pr_m2: 'Superficie (m²)',
        pr_num_parking: 'Estacionamientos',
      };
      return map[key] || key;
    },
    coHeaderLabel(key) {
      const map = {
        co_fecha_inicio: 'Inicio',
        co_tipo: 'Tipo',
        co_monto_clp: 'Monto CLP',
        co_monto_uf: 'Monto UF',
        co_dia_de_pago: 'Día de Pago',
        co_fecha_termino: 'Término',
        co_dias_aviso_termino: 'Días Aviso Término',
        co_garantia: 'Garantía',
        co_fecha_garantia: 'Fecha Garantía',
        co_inventario: 'Inventario',
        co_cuenta_deposito: 'Cuenta Depósito',
        co_vigente: 'Vigente',
        co_notas: 'Notas',
        co_fecha_aviso: 'Fecha Aviso',
      };
      return map[key] || key;
    },
    openPagoForm() {
      this.showPagoForm = true;
    },
    openEditContrato() {
      this.editError = '';
      this.editForm = {
        co_fecha_inicio: this.contrato.co_fecha_inicio || '',
        co_fecha_termino: this.contrato.co_fecha_termino || '',
        co_tipo: this.contrato.co_tipo,
        co_monto_clp: this.contrato.co_monto_clp,
        co_monto_uf: this.contrato.co_monto_uf,
        co_dia_de_pago: this.contrato.co_dia_de_pago,
        co_dias_aviso_termino: this.contrato.co_dias_aviso_termino,
        co_fecha_aviso: this.contrato.co_fecha_aviso || '',
        co_garantia: this.contrato.co_garantia,
        co_fecha_garantia: this.contrato.co_fecha_garantia || '',
        co_cuenta_deposito: this.contrato.co_cuenta_deposito,
        co_inventario: this.contrato.co_inventario || '',
        co_vigente: this.contrato.co_vigente,
        co_notas: this.contrato.co_notas || '',
      };
      this.showEditContrato = true;
    },
    async saveEditContrato() {
      this.editError = '';
      this.savingEdit = true;
      try {
        const payload = {
          ...this.editForm,
          co_monto_clp: Number(this.editForm.co_monto_clp),
          co_monto_uf: Number(this.editForm.co_monto_uf),
          co_dia_de_pago: Number(this.editForm.co_dia_de_pago),
          co_dias_aviso_termino: Number(this.editForm.co_dias_aviso_termino),
          co_garantia: Number(this.editForm.co_garantia),
          co_fecha_aviso: this.editForm.co_fecha_aviso || null,
          co_fecha_garantia: this.editForm.co_fecha_garantia || null,
          co_inventario: this.editForm.co_inventario || null,
          co_notas: this.editForm.co_notas || null,
        };
        const res = await apiClient.patch(`/arriendos/contratos/${this.contrato.co_id}`, payload);
        Object.assign(this.contrato, res.data);
        this.showEditContrato = false;
      } catch (err) {
        this.editError = err.response?.data?.detail || 'No se pudo guardar el contrato.';
      } finally {
        this.savingEdit = false;
      }
    },
    async handlePagoSubmit(newPago) {
      try {
        await apiClient.post('/arriendos/pagos', newPago);
        // Refresh pagos and cobroPago from backend
        const response = await apiClient.get(`/arriendos/contratos/${this.contrato.co_id}/detalle`);
        this.pagos = response.data.pagos;
        this.cobroPago = response.data.cop || response.data.cobropago || [];
      } catch (err) {
        this.error = 'No se pudo guardar el pago';
      } finally {
        this.showPagoForm = false;
      }
    },
    async cargarCobros(force = false) {
      if (!this.fechaCargarCobros) return;
      if (!force && !window.confirm(`¿Confirma cargar los cobros de este contrato a partir del ${this.fechaCargarCobros}?`)) {
        return;
      }
      this.cargarCobrosError = '';
      this.cargandoCobros = true;
      try {
        const res = await apiClient.post(`/arriendos/contratos/${this.contrato.co_id}/cargar-cobros`, {
          fecha_inicio: this.fechaCargarCobros,
          force,
        });
        if (res.data.status === 'conflict') {
          // Ya existen cobros desde esa fecha: pedir confirmación explícita antes de duplicar.
          const periodos = (res.data.cobros_existentes || []).map(c => c.cb_periodo || c.cb_fecha_vencimiento).join(', ');
          const proceder = window.confirm(
            `${res.data.message}\nPeríodos existentes: ${periodos}\n\n¿Cargar de todas formas? Esto puede generar cobros duplicados.`
          );
          if (proceder) {
            await this.cargarCobros(true);
          }
          return;
        }
        const response = await apiClient.get(`/arriendos/contratos/${this.contrato.co_id}/detalle`);
        this.cobros = response.data.cobros;
        this.cobroPago = response.data.cop || response.data.cobropago || [];
        this.showCargarCobros = false;
        this.fechaCargarCobros = '';
      } catch (err) {
        this.cargarCobrosError = err.response?.data?.detail || 'No se pudieron cargar los cobros.';
      } finally {
        this.cargandoCobros = false;
      }
    },
    goToPago(row) {
      // Pass cb_periodo, co_id, cb_id to registrar-pago
      this.$router.push({
        name: 'RegistrarPago',
        params: {
          co_id: this.contrato.co_id,
          cb_id: row.cb_id,
        },
        query: {
          cb_periodo: row.cb_periodo || '',
          cb_monto_uf: row.cb_monto_uf || ''
        }
      });
    },
    isPastDue(fecha) {
      if (!fecha) return false;
      const venc = new Date(fecha);
      const today = new Date();
      return venc < today;
    },
    isFutureDue(fecha) {
      if (!fecha) return false;
      const venc = new Date(fecha);
      const today = new Date();
      return venc >= today;
    },
  },
  async created() {
    const contratoId = this.$route.params.contrato_id;
    try {
      const response = await apiClient.get(`/arriendos/contratos/${contratoId}/detalle`);
      this.contrato = Array.isArray(response.data.contrato) ? response.data.contrato[0] : response.data.contrato;
      this.pagos = response.data.pagos;
      this.cobros = response.data.cobros;
      // Extract cobropago from the detalle endpoint (cop or cobropago key)
      let cobroPagoData = response.data.cop || response.data.cobropago || [];
      if (typeof cobroPagoData === 'string') {
        try {
          cobroPagoData = JSON.parse(cobroPagoData);
        } catch (e) {
          cobroPagoData = [];
        }
      }
      if (Array.isArray(cobroPagoData)) {
        this.cobroPago = cobroPagoData;
      } else if (cobroPagoData && Array.isArray(cobroPagoData.data)) {
        this.cobroPago = cobroPagoData.data;
      } else if (cobroPagoData && typeof cobroPagoData === 'object') {
        this.cobroPago = [cobroPagoData];
      } else {
        this.cobroPago = [];
      }
    } catch (err) {
      this.error = 'No se pudo cargar el detalle del contrato';
    } finally {
      this.loading = false;
    }
  }
};
</script>

<style scoped>
.detail-table {
  width: 100%;
  margin-bottom: 1rem;
  border-collapse: collapse;
}
.detail-table .item {
  font-weight: bold;
  width: 40%;
  background: #f4f4f4;
}
.detail-table .value {
  width: 60%;
}
.side-by-side {
  display: flex;
  gap: 2rem;
  margin-bottom: 1rem;
}
.side-by-side > div {
  flex: 1;
}
.side-by-side-tables {
  display: flex;
  gap: 2rem;
  margin-top: 2rem;
}
.side-by-side-tables > div {
  flex: 1;
}
.data-table {
  width: 100%;
  border-collapse: collapse;
}
.data-table thead {
  background-color: #333;
  color: #fff;
}
.data-table th, .data-table td {
  border: 1px solid #ddd;
  padding: 8px;
}
.odd-row {
  background-color: #f9f9f9;
}
.even-row {
  background-color: #fff;
}
.horizontal-table {
  margin-bottom: 2rem;
}
.vertical-table-container {
  max-width: 400px;
  margin-bottom: 2rem;
}
.vertical-table {
  width: 100%;
  border-collapse: collapse;
}
.vertical-table th {
  background: #f4f4f4;
  font-weight: bold;
  width: 40%;
  text-align: left;
  border: 1px solid #ddd;
  padding: 8px;
}
.vertical-table td {
  width: 60%;
  border: 1px solid #ddd;
  padding: 8px;
}
.pagos-table-container {
  max-width: 500px;
}
.button-71 {
  background-color: #0078d4;
  border: none;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.08), 0 0.5px 1.5px rgba(0,0,0,0.08);
  color: #fff;
  cursor: pointer;
  display: inline-block;
  font-family: "Segoe UI", Arial, sans-serif;
  font-size: 1rem;
  font-weight: 600;
  padding: 0.7em 1.5em;
  margin: 0.2em 0;
  transition: background 0.2s, box-shadow 0.2s, transform 0.1s;
  outline: none;
}
.button-71:hover, .button-71:focus {
  background-color: #005fa3;
  box-shadow: 0 4px 8px rgba(0,0,0,0.12), 0 1.5px 3px rgba(0,0,0,0.12);
  transform: translateY(-2px) scale(1.03);
}
.button-71:active {
  background-color: #004c87;
  box-shadow: 0 1px 2px rgba(0,0,0,0.10);
  transform: none;
}
.button-71.small-btn {
  font-size: 0.8rem;
  padding: 0.5em 1em;
}
.row-red {
  background-color: #ffe6e6 !important;
}
.row-green {
  background-color: #e6ffe6 !important;
}
.co-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.monto-destacado {
  font-weight: bold;
}
.cargar-cobros-form {
  background: #f0f9ff;
  border-radius: 8px;
  padding: 1em 1.5em;
  margin-bottom: 1em;
  max-width: 400px;
}
.cargar-cobros-form label {
  display: block;
  margin-bottom: 0.5em;
  font-weight: 600;
}
.cargar-cobros-form input {
  width: 100%;
  padding: 0.5em;
  border-radius: 6px;
  border: 1px solid #bbb;
  font-size: 1em;
  box-sizing: border-box;
  margin-bottom: 1em;
}
.cargar-cobros-actions {
  display: flex;
  gap: 1em;
}
.edit-contrato-form > div {
  margin-bottom: 0.8em;
}
.edit-contrato-form label {
  display: block;
  margin-bottom: 0.3em;
  font-weight: 600;
}
.edit-contrato-form input,
.edit-contrato-form select,
.edit-contrato-form textarea {
  width: 100%;
  padding: 0.5em;
  border-radius: 6px;
  border: 1px solid #bbb;
  font-size: 1em;
  box-sizing: border-box;
}
.error-msg {
  color: #dc2626;
  font-weight: 600;
}
.button-71.green-btn {
  background-color: #22c55e !important;
  color: #fff;
  border-radius: 12px !important;
}
.button-71.grey-btn {
  background-color: #888 !important;
  color: #fff;
  border-radius: 12px !important;
}
</style>
